import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X } from 'lucide-react';
import './AddNewTrack.css';

const AddNewTrack = ({ 
  isVisible, 
  onClose, 
  onAddTrack, 
  selectedAlbumName, 
  user, 
  language,
  isFullScreen,
  SERVER_ROOT
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  const [videoTitle, setVideoTitle] = useState('');
  const [albumName, setAlbumName] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [isLoadingTitle, setIsLoadingTitle] = useState(false);
  const [error, setError] = useState(null);
  const [videoNotFound, setVideoNotFound] = useState(false);

  // 앨범 이름이 변경될 때 입력 필드의 값도 업데이트
  useEffect(() => {
    // 선택된 앨범이 'all'이 아닌 경우, 해당 앨범 이름으로 설정
    if (selectedAlbumName !== 'all') {
      setAlbumName(selectedAlbumName);
    } else {
      setAlbumName('');
    }
  }, [selectedAlbumName]);

  // YouTube URL이 변경될 때 비디오 정보 가져오기
  useEffect(() => {
    const fetchVideoInfo = async () => {
      // URL이 충분히 길고 YouTube URL 형식인 경우에만 처리
      if (newUrl.length < 10) return;
      
      const videoId = extractYouTubeId(newUrl);
      if (!videoId) return;
      
      setIsLoadingTitle(true);
      setError(null);
      
      try {
        // 서버 API를 통해 비디오 정보 가져오기
        const response = await axios.get(`${SERVER_ROOT}/api/youtube/video-info/${videoId}`);
        
        if (response.data && response.data.title) {
          setVideoTitle(response.data.title);
          setVideoNotFound(false); // 비디오를 찾았으므로 상태 초기화
        } else {
          console.log('비디오 제목을 찾을 수 없습니다.');
          setVideoNotFound(true); // 비디오를 찾지 못함 상태 설정
          
          // 3초 후 메시지 자동 사라짐
          setTimeout(() => {
            setVideoNotFound(false);
          }, 3000);
        }
      } catch (error) {
        console.error(`비디오 정보 가져오기 오류: ${error.message}`);
        setVideoNotFound(true); // 오류 발생 시에도 비디오를 찾지 못함 상태 설정
        
        // 3초 후 메시지 자동 사라짐
        setTimeout(() => {
          setVideoNotFound(false);
        }, 3000);
      } finally {
        setIsLoadingTitle(false);
      }
    };
    
    // URL이 변경되면 비디오 정보 가져오기
    if (newUrl) {
      fetchVideoInfo();
    }
  }, [newUrl, SERVER_ROOT]);

  // YouTube ID 추출 함수
  const extractYouTubeId = (url) => {
    if (!url) return null;
    
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|shorts\/|live\/|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    
    return (match && match[2].length === 11) ? match[2] : null;
  };

  // 닫기 버튼 핸들러
  const handleClose = () => {
    // 닫힘 애니메이션 시작
    setIsClosing(true);
    
    // 애니메이션 시간(300ms) 후 실제로 닫기
    setTimeout(() => {
      setIsClosing(false);
      onClose();
      // 입력 필드 초기화
      setNewUrl('');
      setVideoTitle('');
      setAlbumName(selectedAlbumName !== 'all' ? selectedAlbumName : '');
      setError(null);
    }, 300);
  };

  // 새 링크 추가 처리
  const handleAddNewLink = async () => {
    if (!newUrl || !user || !user.email) {
      setError('URL과 사용자 정보가 필요합니다.');
      return;
    }
    
    try {
      // YouTube ID 추출 검증
      const videoId = extractYouTubeId(newUrl);
      if (!videoId) {
        setError('유효한 YouTube URL이 아닙니다.');
        return;
      }
    
      // 비디오 제목이 비어 있을 경우
      if (!videoTitle) {
        setError('비디오 제목을 입력해주세요.');
        return;
      }
    
      // 앨범 이름 처리 - 없을 경우 현재 선택된 앨범 사용 (all이면 '기본 앨범')
      const effectiveAlbumName = albumName.trim() || 
        (selectedAlbumName !== 'all' ? selectedAlbumName : '기본 앨범');
    
      const response = await axios.post(`${SERVER_ROOT}/api/youtubePlay`, {
        url: newUrl,
        videoTitle: videoTitle,
        userEmail: user.email,
        userName: user.displayName,
        albumName: effectiveAlbumName,
        isPublic: isPublic
      });
    
      if (response.data) {
        // 부모 컴포넌트에 새 트랙 추가 알림
        if (typeof onAddTrack === 'function') {
          onAddTrack(response.data);
        }
        
        // 입력 필드 초기화
        setNewUrl('');
        setVideoTitle('');
        setAlbumName(selectedAlbumName !== 'all' ? selectedAlbumName : '');
        setError(null);
        
        // 닫기
        handleClose();
      }
    } catch (err) {
      console.error(`링크 추가 오류: ${err.message}`);
      setError('YouTube 링크를 추가하는 중 오류가 발생했습니다.');
    }
  };

  // 플레이리스트가 표시되지 않으면 null 반환
  if (!isVisible && !isClosing) return null;

  // 맞춤형 클래스 이름 생성
  const overlayClassName = `add-track-overlay ${isFullScreen ? 'fullscreen-mode' : 'normal-mode'}`;
  const formClassName = `add-track-form ${isFullScreen ? 'fullscreen-form' : 'normal-form'}`;

  return (
    <div className={overlayClassName}>
      <div className={formClassName}>
        <div className="add-track-header">
          <h4>{language !== 'KR' ? "Register a new YouTube URL" : "Youtube URL 등록하기"}</h4>
          <button className="close-form-button" onClick={handleClose}>
            <X size={16} />
          </button>
        </div>
        
        {error && (
          <div className="add-track-error">
            {error}
          </div>
        )}
        
        <div className="add-track-content">
        <div className="form-group">
            <label>{language !== 'KR' ? "Album Name" : "앨범 이름"}</label>
            <input
              type="text"
              placeholder={selectedAlbumName !== 'all' ? selectedAlbumName : (language !== 'KR' ? "Select album, or Write a new album name." : "앨범을 선택하세요. 새 앨범을 생성하려면 앨범 이름을 입력하세요.")}
              value={albumName}
              onChange={(e) => setAlbumName(e.target.value)}
              className="youtube-input"
            />
          </div>

          <div className="form-group">
            <label>{language !== 'KR' ? "Video Title" : "비디오 제목"}</label>
            <input
              type="text"
              placeholder={language !== 'KR' ? "Video title is automatically fetched." : "비디오 제목은 자동으로 가져옵니다."}
              value={videoTitle}
              onChange={(e) => setVideoTitle(e.target.value)}
              className="youtube-input"
              disabled={isLoadingTitle}
            />
            {isLoadingTitle && (
              <div className="loading-indicator">
                {language !== 'KR' ? "Loading video info..." : "비디오 정보 불러오는 중..."}
              </div>
            )}
            {videoNotFound && (
              <div className="video-not-found-message">
                {language !== 'KR' ? "Video information not found. Please check URL or enter details manually." : "비디오 정보를 찾을 수 없습니다. URL을 확인하거나 수동으로 정보를 입력해주세요."}
              </div>
            )}
          </div>

          <div className="form-group">
            <label>{language !== 'KR' ? "YouTube URL" : "YouTube URL"}</label>
            <input
              type="text"
              placeholder={language !== 'KR' ? "Paste a YouTube URL" : "YouTube URL을 여기에 붙여넣기 하세요"}
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              className="youtube-input"
            />
          </div>
          
          
          
          
          
          <div className="form-group visibility-toggle">
            <label>
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
              />
              {language !== 'KR' ? "Public" : "공개"}
            </label>
          </div>
        </div>
        
        <div className="add-track-footer">
          <button onClick={handleAddNewLink} className="add-button">
            {language !== 'KR' ? "Submit" : "등록"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddNewTrack;